import React, { useEffect, useState, useCallback } from 'react';
import { useAppSelector } from '../../store/hooks';
import { fetchAdminBookings, updateAdminBooking } from '../../api/admin';
import { AdminDataTable, AdminPagination, Column } from '../../components/admin';

const statusOptions = [
  { label: 'All', value: '' },
  { label: 'Booked', value: 'booked' },
  { label: 'Unbooked', value: 'unbooked' },
];

const AdminBookings: React.FC = () => {
  const colors = useAppSelector((state) => state.theme.theme);
  const [bookings, setBookings] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const pageSize = 20;

  const load = useCallback(() => {
    setLoading(true);
    const params: Record<string, string | number> = { page, page_size: pageSize };
    if (statusFilter) params.status = statusFilter;
    fetchAdminBookings(params)
      .then((res) => { setBookings(res.data.results); setTotal(res.data.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleToggleBooked = async (booking: any) => {
    try {
      await updateAdminBooking(booking.id, { is_booked: !booking.is_booked });
      load();
    } catch {}
  };

  const typeMap: Record<string, string> = { V: 'Video', A: 'Audio', C: 'Chat' };

  const columns: Column[] = [
    { key: 'id', header: 'ID', width: '60px' },
    { key: 'user_name', header: 'User' },
    { key: 'expert_name', header: 'Expert' },
    { key: 'booking_type', header: 'Type', width: '80px', render: (row) => typeMap[row.booking_type] || row.booking_type },
    { key: 'start_time', header: 'Date', render: (row) => row.start_time?.split('T')[0] },
    {
      key: 'is_booked', header: 'Status', width: '100px',
      render: (row) => (
        <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{
          backgroundColor: row.is_booked ? '#10B98120' : '#F59E0B20',
          color: row.is_booked ? '#10B981' : '#F59E0B',
        }}>{row.is_booked ? 'Confirmed' : 'Pending'}</span>
      ),
    },
    {
      key: 'actions', header: '', width: '120px',
      render: (row) => (
        <button
          onClick={(e) => { e.stopPropagation(); handleToggleBooked(row); }}
          className="px-2 py-1 rounded text-xs font-medium"
          style={{ backgroundColor: `${colors.primary}15`, color: colors.primary }}
        >
          {row.is_booked ? 'Cancel' : 'Force Accept'}
        </button>
      ),
    },
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6" style={{ color: colors.textColor }}>Bookings</h1>

      <div className="mb-4">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 rounded-xl text-sm outline-none"
          style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}`, color: colors.textColor }}
        >
          {statusOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      <AdminDataTable columns={columns} data={bookings} loading={loading} emptyMessage="No bookings found" />
      <AdminPagination page={page} totalPages={Math.ceil(total / pageSize)} onPageChange={setPage} />
    </div>
  );
};

export default AdminBookings;
